import React from "react";
var __jsx = React.createElement;
import { useEffect, useState, useMemo } from "react";
import { useLang } from "@/common/Contexts/LanguageContext";
import { trackEvents } from "@/common/events/events";
import { getPlatformInfo } from "@/utils/GlobalJavascriptFunction";
import useHelpers from "@hooks/useHelpers";
import useBrandsData from "@hooks/useBrandsData";
import { ucWords } from "@/utils/stringHelpers";
import BenefitsComparison from "./components/BenefitsComparison";
import FAQSection from "./components/FAQSection";
import FinalCTASection from "./components/FinalCTASection";
import IndicatorsCards from "@/pages/sell-trade-v2/sell-trade/components/IndicatorsCards";
import CarsMakes from "@/pages/sell-trade-v2/sell-trade/components/CarsMakes/CarsMakes";
import SellCarSteps from "@/pages/sell-trade-v2/sell-trade/components/SellCarSteps";
import FlatBanner from "@/pages/sell-trade-v2/sell-trade/components/FlatBanner";
import CarSaleHeroSection from "@/pages/sell-trade-v2/sell-trade/components/CarSaleHeroSection";
import { useFullPageData } from "@/common/Contexts/FullPageContext";
import { URL_SELL_TRADE_FORM_v2 } from "@/pages/sell-trade-v2/sell-trade/constants";
import arData from "@data/landing-pages/sell-trade-v3/ar.json";
import enData from "@data/landing-pages/sell-trade-v3/en.json";
import { useAuthActions, useSession } from "@/_MiniApps/auth/AuthContext";
import SellCarHero from "@/pages/sell-trade-v2/sell-trade/components/SellCarHero";
import useCheckABFlag from "@hooks/useCheckABFlag";

// API Response Interfaces

var SellTradeLanding = function SellTradeLanding(_ref) {
  var _finalCtaData$title, _finalCtaData$subtitl, _finalCtaData$payload, _finalCtaData$payload2, _finalCtaData$payload3;
  var initialData = _ref.initialData;
  var lang = useLang();
  var _useHelpers = useHelpers(),
    navigateTo = _useHelpers.navigateTo;
  var fullPageData = useFullPageData();

  // Use fullPageData if available, otherwise fallback to static JSON data
  var fallbackData = useMemo(function () {
    return lang === "ar" ? arData : enData;
  }, [lang]);
  var contentData = (fullPageData === null || fullPageData === void 0 ? void 0 : fullPageData.data) || (fallbackData === null || fallbackData === void 0 ? void 0 : fallbackData.contentSections);

  // Initialize with pre-filled data from URL params or default
  var _useState = useState(function () {
      if (typeof window !== "undefined") {
        var urlParams = new URLSearchParams(window.location.search);
        return urlParams.get("make") || "";
      }
      return "";
    }),
    selectedMake = _useState[0],
    setSelectedMake = _useState[1];
  var _useState2 = useState(function () {
      if (typeof window !== "undefined") {
        var urlParams = new URLSearchParams(window.location.search);
        return urlParams.get("model") || "";
      }
      return "";
    }),
    selectedModel = _useState2[0],
    setSelectedModel = _useState2[1];
  var _useState3 = useState(function () {
      if (typeof window !== "undefined") {
        var urlParams = new URLSearchParams(window.location.search);
        return urlParams.get("trim") || "";
      }
      return "";
    }),
    selectedTrim = _useState3[0],
    setSelectedTrim = _useState3[1];
  var _useState4 = useState(function () {
      if (typeof window !== "undefined") {
        var urlParams = new URLSearchParams(window.location.search);
        return urlParams.get("year") || "";
      }
      return "";
    }),
    selectedYear = _useState4[0],
    setSelectedYear = _useState4[1];
  var _useBrandsData = useBrandsData(),
    makes = _useBrandsData.makes,
    getExtensionsByModelId = _useBrandsData.getExtensionsByModelId,
    getModelById = _useBrandsData.getModelById;

  // Transform data for components
  var carMakes = useMemo(function () {
    return makes.map(function (make) {
      return {
        id: make.id,
        name: ucWords(make.name),
        logo: make.logo,
        seo_url: make.seo_url
      };
    });
  }, [makes]);
  var carTrims = useMemo(function () {
    if (!selectedModel || !selectedMake) return [];
    var modelId = parseInt(selectedModel);
    var makeId = parseInt(selectedMake);
    return getExtensionsByModelId(modelId, makeId).map(function (trim) {
      return {
        id: trim.id,
        name: ucWords(trim.name)
      };
    });
  }, [selectedModel, selectedMake, getExtensionsByModelId]);

  // Get available years based on selected model
  var getYearsForModel = function getYearsForModel(modelId) {
    if (!modelId) return [];
    var modelData = getModelById(parseInt(modelId));
    var currentYear = new Date().getFullYear();

    // For newer models (introduced after 2020), limit years from 2018
    // For older models, use full range from 2016
    var startYear = modelData && modelData.id > 1000 ? 2018 : 2016;
    return Array.from({
      length: currentYear + 2 - startYear
    }, function (v, i) {
      return (currentYear + 1 - i).toString();
    });
  };
  var availableYears = useMemo(function () {
    return selectedModel ? getYearsForModel(selectedModel) : [];
  }, [selectedModel, getModelById]);
  useEffect(function () {
    // Track the landing page view for analytics
    trackEvents("enhanced_sell_trade_landing_view", {
      platform: getPlatformInfo()
    });

    // Reset model and trim when make changes
    setSelectedModel("");
    setSelectedTrim("");
    setSelectedYear(""); // Also reset year when make changes
  }, [selectedMake]);
  useEffect(function () {
    // Reset year when model changes
    setSelectedYear("");
  }, [selectedModel]);
  var isABTestEnabled = useCheckABFlag("AB_SELL_MY_CAR_V3");
  var handleStartProcess = function handleStartProcess() {
    var params = {};
    if (selectedMake) {
      params.make_id = selectedMake;
    }
    // Track the CTA click event
    trackEvents("sell_car_form_landing", {
      platform: getPlatformInfo(),
      referral_location: "bottom_cta",
      version: isABTestEnabled && "3"
    });
    // Navigate to the form page
    navigateTo(URL_SELL_TRADE_FORM_v2, params);
  };

  // Helper functions to extract components from API response
  var getComponentByType = function getComponentByType(componentName, componentType) {
    if (!contentData || contentData.length === 0) return null;
    return contentData.find(function (comp) {
      if (componentType) {
        return comp.__component === componentName && comp.component_type === componentType;
      }
      return comp.__component === componentName;
    });
  };

  // Extract data from API response or use fallback hardcoded data
  var sellCarHeroData = getComponentByType("sell-trade.sell-trade-hero");
  var carMakesData = getComponentByType("shared.placeholder", "car_makes_list");
  var sellCarStepsData = getComponentByType("shared.placeholder", "sell_trade_steps");
  var indicatorsData = getComponentByType("shared.placeholder", "indicators");
  var flatBannerData = getComponentByType("elements.image-responsive");
  var benefitsComparisonData = getComponentByType("shared.table-block");
  var faqData = getComponentByType("shared.faq-list");
  var finalCtaData = getComponentByType("shared.placeholder", "flow_entrypoint_cta");
  var _useSession = useSession(),
    isLoggedIn = _useSession.isLoggedIn;
  var _useAuthActions = useAuthActions(),
    isAuthInitialized = _useAuthActions.isAuthInitialized;

  // Add delay to ensure auth state is fully stable before rendering
  var _useState5 = useState(false),
    isAuthReady = _useState5[0],
    setIsAuthReady = _useState5[1];
  useEffect(function () {
    if (isAuthInitialized()) {
      // Wait a brief moment for auth state to fully stabilize
      var timer = setTimeout(function () {
        setIsAuthReady(true);
      }, 100);
      return function () {
        return clearTimeout(timer);
      };
    }
  }, [isAuthInitialized()]);
  return /*#__PURE__*/React.createElement("div", {
    id: "sell-trade-landing-container",
    className: "text-[#333] w-full overflow-x-hidden"
  }, /*#__PURE__*/React.createElement(React.Fragment, null, sellCarHeroData && (!isAuthReady ?
  /*#__PURE__*/
  // Render logged-out hero during SSR and before auth is ready,
  // ensuring H1 is present in the initial HTML response for SEO
  React.createElement(SellCarHero, {
    data: sellCarHeroData
  }) : isLoggedIn ? /*#__PURE__*/React.createElement(CarSaleHeroSection, {
    autoPlay: false,
    data: sellCarHeroData,
    autoPlayInterval: 5000,
    onBookAppointment: function onBookAppointment(request_id) {
      return navigateTo(URL_SELL_TRADE_FORM_v2, {
        request_id: request_id,
        landing_page: true
      });
    }
  }) : /*#__PURE__*/React.createElement(SellCarHero, {
    id: "sell-trade-landing-hero",
    data: sellCarHeroData
  })), /*#__PURE__*/React.createElement(CarsMakes, {
    id: "sell-trade-landing-cars-makes"
  }), sellCarStepsData && /*#__PURE__*/React.createElement(SellCarSteps, {
    id: "sell-trade-landing-sell-car-steps",
    data: sellCarStepsData
  }), indicatorsData && /*#__PURE__*/React.createElement(IndicatorsCards, {
    id: "sell-trade-landing-indicators-cards",
    data: indicatorsData
  }), flatBannerData && /*#__PURE__*/React.createElement("div", {
    id: "sell-trade-landing-flat-banner-container",
    className: "max-w-[1200px] mx-auto md:px-0 px-4 my-8 "
  }, /*#__PURE__*/React.createElement(FlatBanner, {
    id: "sell-trade-landing-flat-banner",
    data: flatBannerData
  })), benefitsComparisonData && /*#__PURE__*/React.createElement(BenefitsComparison, {
    id: "sell-trade-landing-benefits-comparison",
    data: benefitsComparisonData
  }), faqData && /*#__PURE__*/React.createElement(FAQSection, {
    id: "sell-trade-landing-faq-section",
    data: faqData
  }), /*#__PURE__*/React.createElement(FinalCTASection, {
    id: "sell-trade-landing-final-cta-section",
    selectedMake: selectedMake,
    selectedTrim: selectedTrim,
    selectedYear: selectedYear,
    onMakeChange: function onMakeChange(makeId) {
      setSelectedMake(makeId);
      setSelectedModel("");
      setSelectedTrim("");
    },
    onTrimChange: setSelectedTrim,
    onYearChange: setSelectedYear,
    onStartProcess: handleStartProcess,
    carMakes: carMakes,
    carTrims: carTrims,
    carYears: availableYears,
    title: (_finalCtaData$title = finalCtaData === null || finalCtaData === void 0 ? void 0 : finalCtaData.title) !== null && _finalCtaData$title !== void 0 ? _finalCtaData$title : "",
    subtitle: (_finalCtaData$subtitl = finalCtaData === null || finalCtaData === void 0 ? void 0 : finalCtaData.subtitle) !== null && _finalCtaData$subtitl !== void 0 ? _finalCtaData$subtitl : "",
    ctaLabel: finalCtaData === null || finalCtaData === void 0 ? void 0 : (_finalCtaData$payload = finalCtaData.payloads[0]) === null || _finalCtaData$payload === void 0 ? void 0 : (_finalCtaData$payload2 = _finalCtaData$payload.cta) === null || _finalCtaData$payload2 === void 0 ? void 0 : (_finalCtaData$payload3 = _finalCtaData$payload2[0]) === null || _finalCtaData$payload3 === void 0 ? void 0 : _finalCtaData$payload3.title
  })));
};
export default SellTradeLanding;